import { useState, useEffect, useRef } from"react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { useTranslation } from"react-i18next";
import { taskTypeTileShadow } from "@/lib/taskTypeIcon";
import { PersonAvatar } from "@/components/ui/PersonAvatar";
import { useNavigate } from"react-router-dom";
import { formatDateLong, formatDate } from"@/lib/dateFormatters";
import { supabase } from"@/integrations/supabase/client";
import { useAuth } from"@/contexts/AuthContext";
import {
 Drawer,
 DrawerContent,
 DrawerHeader,
 DrawerTitle,
 DrawerClose,
} from"@/components/ui/drawer";
import { SHEET_CLOSE_CLASS, SHEET_SAFE_PB_CLASS, SheetCloseIcon } from"@/components/ui/sheetChrome";
import { MiniAudioPlayer } from"@/components/ui/MiniAudioPlayer";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { PILL_BASE, PILL_COLORS } from"@/lib/statusPill";
import { Separator } from"@/components/ui/separator";
import { ScrollArea } from"@/components/ui/scroll-area";
import { Input } from"@/components/ui/input";
import { useToast } from"@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from"@/components/ui/alert-dialog";

import { Calendar, User, MapPin, Bug, FileText, Edit, Trash2, Rows, Camera, Loader2, Mic, CheckCircle2, House, ChevronDown, Crosshair, TrendingUp, TrendingDown, Minus, ShieldCheck, HelpCircle, PencilLine } from"lucide-react";
import { usePestTranslation } from"@/hooks/usePestTranslation";
import { fileToBase64 } from"@/lib/imageCompression";
import { estadoDiagnostico, limpiarNombrePlaga, seEnseña } from "@/lib/pestDiagnosis";
import { analisisEnMarcha, idiomaDelAnalisis, inicioDelAnalisis, MAX_FOTOS_ANALISIS, pedirAnalisis, sePuedeGuardarEnElParte, type AiAnalysisData } from "@/lib/pests/photoAnalysisRun";
import { AiAnalysisProgress } from "@/components/pests/AiAnalysisProgress";
import { AiNoPestMessage, AiOutcomeChips } from "@/components/pests/AiOutcomeChips";
import { leerDesenlace } from "@/lib/pests/desenlaceIA";
import { ScoutingTriagePanel, ACTION_CLASS, actionStyle, ACTION_COLORS } from"./ScoutingTriagePanel";
import { Skeleton } from "@/components/ui/skeleton";
import { useSheetSettled } from "@/hooks/use-sheet-settled";
import { ImageLightbox } from"@/components/ui/ImageLightbox";
import { useSessionFocus } from "@/hooks/useSessionFocus";
import { useFocusTimeline } from "@/hooks/useFocusTimeline";
import { useFollowUpReference } from "@/hooks/useFollowUpReference";
import { todayIso } from "@/lib/reports/dates";
import { FocusUpdateDialog } from "@/components/health-map/FocusUpdateDialog";
import { RequestFollowUpDialog } from "@/components/health-map/RequestFollowUpDialog";
import { sessionReportOf, reportVerdict, type ReportVerdict, type SessionReportRow } from "@/lib/pests/sessionReport";
import { LINE_STATE_INK } from "@/components/health-map/MapLayers";
import { SEVERITY_COLORS } from "@/components/reports/shell/tokens";
import { sessionStatusOf } from "@/lib/pests/sessionStatus";
import { ImgOffline } from "@/components/offline/ImgOffline";


/* Tarjetas de icono: color sólido + icono blanco (lenguaje visual de la app).
 Azul y naranja salen del mapa de src/lib/taskTypeIcon.ts. */
const TILE_BLUE = "#2B7DE0";
const TILE_GREEN = "#059669";
const TILE_VIOLET = "#8B5CF6";

/* «Cómo está»: el mismo sentido que el ▲▬▼ del mapa (arriba = empeora). */
const VERDICT_TILE: Record<ReportVerdict, { color: string; Icon: typeof Minus }> = {
 worse: { color: "#DC2626", Icon: TrendingUp },
 better: { color: TILE_GREEN, Icon: TrendingDown },
 same: { color: "#64748B", Icon: Minus },
 gone: { color: "#047857", Icon: ShieldCheck },
 unknown: { color: "#94A3B8", Icon: HelpCircle },
};
const TILE_NEUTRAL = "#94A3B8";


/* ¿Hay algo que enseñar? La regla vive en `lib/pestDiagnosis` y es la MISMA que
   la del chat y el escáner (18-09-2026). Antes aquí se escondía todo lo que bajaba
   de 60 % y se decía «No se reconoció ninguna plaga»: la IA había visto un virus
   probable y el parte decía lo contrario. Ahora por debajo de 60 % se enseña como
   sospecha, y «no se reconoció» solo sale cuando de verdad no vio nada. */
const hasUsableAnalysis = (a: AiAnalysisData | null) => seEnseña(estadoDiagnostico(a));

function AiAnalysisCard({ data, media, onReanalyze }: { data: AiAnalysisData; media?: React.ReactNode; onReanalyze?: () => void }) {
 const { t } = useTranslation();
 const pct =
 typeof data.confidence ==="number"
 ? Math.round((data.confidence > 1 ? data.confidence : data.confidence * 100))
 : null;
 const severityColor = (sev?: string) => {
 switch ((sev ||"").toLowerCase()) {
 case"low": return PILL_COLORS.blue;
 case"medium": return PILL_COLORS.amber;
 case"high": return PILL_COLORS.red;
 case"critical": return PILL_COLORS.red;
 default: return PILL_COLORS.slate;
 }
 };
 // Plegada por defecto: el resumen (plaga, nombre científico, gravedad y confianza)
 // es lo que se mira siempre; el texto largo, solo cuando se quiere.
 const [expanded, setExpanded] = useState(false);
 const hasDetail = !!data.description;
 const sospecha = estadoDiagnostico(data) === "sospecha";
 /* Con la lista del cultivo (F7) la IA también dice «es un AUXILIAR» o «es abiótico», y
    si la plaga está fuera de la lista del cultivo o salió de la vigilancia: se dice con
    pastillas (`AiOutcomeChips`). Un auxiliar no tiene gravedad de plaga, así que no se
    pinta la suya («Ninguna»). La tarjeta solo enseña: no elige plaga ni toca focos (F1). */
 const lectura = leerDesenlace(data);
 const nombre = lectura?.nombre || limpiarNombrePlaga(data.pestName);
 const conGravedad = !sospecha && !!data.severity && lectura?.desenlace !== "auxiliar";
 // `media` (las fotos) entra flotando DENTRO de la tarjeta, no al lado: así el
 // texto largo empieza a la derecha de la foto y sigue por debajo, en vez de
 // quedarse en una columna estrecha con medio hueco en blanco. La cabecera y el
 // botón son flex, o sea que crean contexto propio y se colocan al lado del
 // flotante sin montarse encima; el párrafo sí lo rodea. El after:clear-both
 // evita que la foto se salga de la tarjeta cuando el texto es más corto que ella.
 return (
 <div className="p-3 rounded-lg bg-primary-foreground border border-[#b6dded] space-y-2 after:content-[''] after:block after:clear-both">
 {media}
 <div className="flex items-start gap-2">
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 flex-wrap">
 {nombre && (
 <p className="font-semibold text-foreground leading-tight">
 {sospecha
 ? t("pests.scouting.aiSuspect", "Posible {{name}}, sin confirmar", { name: nombre })
 : nombre}
 </p>
 )}
 {/* Con una sospecha, la gravedad sería inventada: no se pinta. */}
 {conGravedad && (
 <span className={PILL_BASE} style={{ backgroundColor: severityColor(data.severity) }}>
 {t(`worker.scouting.severityLevels.${data.severity}`, data.severity)}
 </span>
 )}
 </div>
 {/* Sin la coletilla, el nombre de un auxiliar ES el científico («Orius laevigatus»): no se repite. */}
 {data.scientificName && data.scientificName.trim().toLowerCase() !== (nombre ?? "").trim().toLowerCase() && (
 <p className="text-xs italic text-muted-foreground leading-tight mt-0.5">{data.scientificName}</p>
 )}
 <AiOutcomeChips lectura={lectura} cropCode={data.cropCode ?? null} className="mt-1" />
 <div className="flex flex-wrap items-center gap-1 mt-1">
 {pct != null && (
 <Badge variant="outline" className="text-xs shrink-0 w-fit">
 {t("pests.scouting.aiConfidenceShort","Confianza {{pct}}%", { pct })}
 </Badge>
 )}
 {/* El acuerdo entre modelos es la señal fuerte: dos análisis independientes
     que coinciden valen más que un porcentaje que se pone a sí mismo. */}
 {data.agreementConfirmed === true && (
 <Badge variant="outline" className="text-xs shrink-0 w-fit border-emerald-300 bg-emerald-50 text-black">
 {t("pests.scouting.aiAgreementConfirmed","Confirmado por 2 modelos")}
 </Badge>
 )}
 {data.agreementConfirmed === false && (
 <Badge variant="outline" className="text-xs shrink-0 w-fit border-amber-300 bg-amber-50 text-black">
 {t("pests.scouting.aiAgreementDisputed","Sin coincidencia entre modelos")}
 </Badge>
 )}
 {/* La foto se analiza UNA vez y el resultado se guarda para todos: repetirlo
     vuelve a pagar y cambia lo que ya han visto los demás, así que solo el
     administrador puede (el servidor lo comprueba otra vez). */}
 {onReanalyze && (
 <button
 type="button"
 onClick={onReanalyze}
 className="shrink-0 text-xs underline text-muted-foreground hover:text-foreground"
 >
 {t("pests.scouting.aiReanalyze","Repetir análisis")}
 </button>
 )}
 </div>
 {sospecha && (
 <div className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
 {!!data.candidates?.length && (
 <p>{t("pests.scouting.aiCandidates", "También podría ser: {{list}}", { list: data.candidates.join(", ") })}</p>
 )}
 <p>{t("pests.scouting.aiNeedsBetterPhoto", "Para confirmarlo, sube una foto de la planta entera, del envés de las hojas y del fruto.")}</p>
 </div>
 )}
 </div>
 </div>

 {hasDetail && (
 <>
 <button
 type="button"
 onClick={() => setExpanded((v) => !v)}
 className="flex items-center gap-1 text-xs font-semibold text-[#2E84DE] hover:underline"
 >
 {expanded
 ? t("pests.scouting.aiAnalysisCollapse","Ocultar análisis")
 : t("pests.scouting.aiAnalysisExpand","Ver análisis completo")}
 <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
 </button>
 {expanded && (
 <>
 <p className="text-sm text-foreground whitespace-pre-wrap">{data.description}</p>
 <p className="text-[11px] text-muted-foreground pt-1 border-t">
 {t("pests.scouting.aiAnalysisDisclaimer")}
 </p>
 </>
 )}
 </>
 )}
 </div>
 );
}

interface ScoutingSession {
 id: string;
 date: string;
 greenhouse_id: string | null;
 block_id: string | null;
 notes: string | null;
 created_at?: string;
 inspector_id?: string | null;
 greenhouse?: { name: string };
 block?: { name: string };
 inspector?: { full_name: string | null; avatar_url?: string | null };
}

interface PestObservation {
 id: string;
 pest_id: string;
 count: number;
 unit: string | null;
 severity: string | null;
 notes: string | null;
 observed_row_start?: number | null;
 observed_row_end?: number | null;
 pest?: { name: string; type: string | null; translation_key?: string | null };
}

interface ScoutingSessionDetailDrawerProps {
 session: ScoutingSession | null;
 open: boolean;
 onOpenChange: (open: boolean) => void;
 onSessionDeleted?: () => void;
 onSessionUpdated?: () => void;
 /**
 * Optional handler called when the manager presses"Crear tratamiento" on a
 * classified session. Receives the current session so the parent can build
 * prefilled task data (greenhouse, block, pest, line). When omitted the
 * triage panel falls back to navigating to /pests?tab=treatments.
 */
 onCreateTreatment?: (session: ScoutingSession) => void;
 /**
 * La sesión todavía viene por el cable. Con esto la hoja SE ABRE IGUAL, con un
 * esqueleto dentro, en vez de dejar el toque sin respuesta hasta que contesta
 * la consulta. Lo usa la ficha del mapa, que abre por id.
 */
 loading?: boolean;
 /**
 * Alto fijo para la hoja. Va de la mano de `loading`: si no se sabe lo que va
 * a caber dentro, hay que comprometerse a un alto o la hoja crece a mitad de
 * animación. Ver `DETAIL_SHEET_H_CLASS`.
 */
 heightClassName?: string;
 /**
 * El foco que está abierto DEBAJO de esta ficha (el mapa abre el muestreo
 * encima de la ficha de su foco). Si es el suyo, la fila «Foco» no lleva
 * botón: cerrar ya devuelve a él.
 */
 currentFocusId?: string | null;
 /**
 * Abrir el foco aquí mismo, sin cambiar de pantalla (el mapa). Sin esto, el
 * botón lleva al mapa (`/blocks`) con la nave y la ficha del foco abiertas.
 */
 onOpenFocus?: (focusId: string) => void;
}

export function ScoutingSessionDetailDrawer({
 session,
 open,
 onOpenChange,
 onSessionDeleted,
 onSessionUpdated,
 onCreateTreatment,
 loading: sessionLoading = false,
 heightClassName,
 currentFocusId = null,
 onOpenFocus,
}: ScoutingSessionDetailDrawerProps) {
 const { t, i18n } = useTranslation();
 const navigate = useNavigate();
 const { role, user } = useAuth();
 const { toast } = useToast();
 const queryClient = useQueryClient();
 const pestTranslation = usePestTranslation();
 const [observations, setObservations] = useState<PestObservation[]>([]);
 const [loading, setLoading] = useState(false);
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
 const [isDeleting, setIsDeleting] = useState(false);
 const [mediaFetchFailed, setMediaFetchFailed] = useState(false);
 const [photoUrl, setPhotoUrl] = useState<string | null>(null);
 const [photoUrls, setPhotoUrls] = useState<string[]>([]);
 const [audioUrl, setAudioUrl] = useState<string | null>(null);
 const [manualMap, setManualMap] = useState(false);
 const [voiceTranscript, setVoiceTranscript] = useState<string | null>(null);
 const [voiceTranscriptLang, setVoiceTranscriptLang] = useState<string | null>(null);
 const [voiceTranslations, setVoiceTranslations] = useState<Record<string, string>>({});
 const [transcribeBusy, setTranscribeBusy] = useState(false);
 const [transcribeFailed, setTranscribeFailed] = useState(false);
 const [showOriginalTranscript, setShowOriginalTranscript] = useState(false);
 const [sessionSeverity, setSessionSeverity] = useState<string | null>(null);
 /* Lo que la base sabe del parte y la pantalla que abre la ficha no siempre
    pasa: las notas (conteos, línea; auditoría T08-08: desde el Inicio salían
    vacías) y quién lo clasificó o descartó y cuándo (D1: queda quién fue). */
 const [fetchedNotes, setFetchedNotes] = useState<string | null>(null);
 const [triagedBy, setTriagedBy] = useState<{ id: string | null; at: string | null; name: string | null }>({ id: null, at: null, name: null });
 const [workerResolvedAt, setWorkerResolvedAt] = useState<string | null>(null);
 const [locationName, setLocationName] = useState<string | null>(null);
 const [aiAnalysis, setAiAnalysis] = useState<AiAnalysisData | null>(null);
 const [triage, setTriage] = useState<{
 triage_status:"pending_triage" |"classified" |"dismissed" |"not_required";
 triage_pest_id: string | null;
 triage_pest_name_snapshot: string | null;
 dismissal_reason: string | null;
 dismissal_note: string | null;
 worker_resolved_at?: string | null;
 review_status?: string | null;
 linked_task_id: string | null;
 } | null>(null);
 /* El foco del parte y lo que dijo quien muestreó (24-09-2026): llegan con la
    misma segunda consulta que las fotos. */
 const [focusId, setFocusId] = useState<string | null>(null);
 const [reportRow, setReportRow] = useState<SessionReportRow | null>(null);
 /* Con qué se comparó al preguntar «¿es lo mismo?» (un foco o un parte suelto). */
 const [followRef, setFollowRef] = useState<{ focusId: string | null; sessionId: string | null }>({ focusId: null, sessionId: null });
 const [reportChangerName, setReportChangerName] = useState<string | null>(null);

 // AI re-analyze dialog (legacy sessions without saved scan)
 // (legacy analyzeDialogOpen state removed — AI analysis now renders inline only)
 const [analyzeBusy, setAnalyzeBusy] = useState(false);
 /** Desde cuándo se analiza (para la barra) y si ya llegó (la barra llega al 100 antes de irse). */
 const [analyzeSince, setAnalyzeSince] = useState<number>(() => Date.now());
 const [analyzeDone, setAnalyzeDone] = useState(false);
 /** El diagnóstico acaba de llegar tras la barra: entra con un fundido. */
 const [analyzeRevealed, setAnalyzeRevealed] = useState(false);
 const [analyzeResult, setAnalyzeResult] = useState<AiAnalysisData | null>(null);
 const [analyzeFailed, setAnalyzeFailed] = useState(false);

 // Individual observation deleting
 const [deletingObservation, setDeletingObservation] = useState<PestObservation | null>(null);
 const [isDeletingObservation, setIsDeletingObservation] = useState(false);
 const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

 const isWorker = role ==="worker";
 /*
  * La ficha subía dando tirones porque TODO lo de abajo (audio, fotos,
  * diagnóstico y el pie de clasificación) llega en una segunda consulta y se
  * monta con altura cero: la hoja crecía a mitad de los 260 ms de la subida.
  *
  * Derivado y no un estado con su efecto: `triage` es lo último que escribe esa
  * consulta, así que «todavía no ha llegado» se sabe ya en el PRIMER render, sin
  * el fotograma sin reservar que dejaría un `useState(false)` encendido después.
  */
 const mediaLoading = open && !!session && !triage && !mediaFetchFailed;
 /*
  * Y el hueco reservado, además, no se suelta hasta que la hoja ha dejado de
  * moverse. El esqueleto tapaba el salto de altura, pero no el trabajo: si la
  * consulta contestaba a los 120 ms, el audio, las miniaturas y el pie se
  * construían —y las fotos se descodificaban— en el mismo hilo que estaba
  * animando la hoja, y la subida se comía fotogramas igual. `useSheetSettled`
  * no retrasa la consulta, solo el momento de pintar lo que traiga: la cabecera
  * y la tarjeta de datos de la sesión, que salen de las props, siguen ahí desde
  * el primer fotograma.
  */
 const settled = useSheetSettled(open);
 const mediaReady = settled && !mediaLoading;
 const canManage = role ==="admin" || role ==="manager" ||
 (session?.inspector_id && user?.id && session.inspector_id === user.id);
 /* D1 (Pablo, 26-09-2026): el perito clasifica y descarta TODOS los partes de
    su empresa, como un encargado sin recorte (la base lo permite desde la
    migración 20260926170000). Borrar observaciones sigue siendo de `canManage`. */
 const canTriage = canManage || role === "perito";

 /* El foco del parte. Al trabajador no se le enseña: para él no existe la
    palabra «foco» (F5). */
 const { data: focusData } = useSessionFocus(open && !isWorker ? focusId : null);
 /* UN SOLO PARTE NO ES UN FOCO (Pablo, 27-09-2026: «no hay foco cuando hay una sola
    sesión»). Mientras el parte está solo, ni fila «Foco» ni botón para ir a él; lo
    que se hacía desde la ficha del foco —añadir una actualización, pedir un
    seguimiento— se hace aquí mismo. En cuanto se le junta otro, es un foco y la
    fila vuelve. */
 const soloParte = !!focusData && focusData.samplings <= 1;
 const focusInfo = focusData && !soloParte ? focusData : null;
 const canActSolo = soloParte && (role === "admin" || role === "manager");
 const { data: soloFocus } = useFocusTimeline(open && canActSolo ? focusData!.id : null);
 const [soloFollowOpen, setSoloFollowOpen] = useState(false);
 const [soloUpdateOpen, setSoloUpdateOpen] = useState(false);
 const report = sessionReportOf(reportRow);
 // Solo cuando contestó «es otra cosa» o «no sé si es lo mismo»: ahí hace falta saber de QUÉ.
 const needsRef = report?.kind === "answer" && report.answer !== "same";
 const { data: refInfo } = useFollowUpReference(
 needsRef ? followRef : { focusId: null, sessionId: null },
 session ? { id: session.id, date: session.date ?? null } : null,
 );
 /* A dónde lleva la fila del foco: nada si ya estamos dentro de él (se cierra
    y se vuelve), abrirlo aquí si la pantalla sabe (el mapa), o ir al mapa. El
    mapa es de admin y encargado (`ManagerGuard`). */
 const focusLink: "none" | "here" | "map" =
 !focusInfo || focusInfo.id === currentFocusId ? "none"
 : onOpenFocus ? "here"
 : role === "admin" || role === "manager" ? "map"
 : "none";
 const openFocus = () => {
 if (!focusInfo) return;
 onOpenChange(false);
 if (focusLink === "here") {
 onOpenFocus?.(focusInfo.id);
 return;
 }
 // Por el estado de la navegación y no por la URL: en frío el saludo se come los parámetros.
 navigate("/blocks", {
 state: {
 selectedLocationId: focusInfo.locationId ?? undefined,
 openGreenhouseId: focusInfo.greenhouseId,
 openFocusId: focusInfo.id,
 },
 });
 };

 /* Quién lo clasificó o descartó: el nombre llega aparte para no retrasar la
    ficha (el esqueleto espera a `triage`). */
 useEffect(() => {
 if (!open || !triagedBy.id || triagedBy.name) return;
 let cancelled = false;
 (async () => {
 const { data } = await supabase.from("profiles").select("full_name").eq("user_id", triagedBy.id).maybeSingle();
 const name = (data as { full_name?: string | null } | null)?.full_name ?? null;
 if (!cancelled && name) setTriagedBy((prev) => (prev.id === triagedBy.id ? { ...prev, name } : prev));
 })();
 return () => { cancelled = true; };
 }, [open, triagedBy.id, triagedBy.name]);

 /* Si el avance lo cambió otra persona después, se dice quién. */
 const changedBy = report && report.kind !== "not_asked" ? report.changedBy : null;
 useEffect(() => {
 setReportChangerName(null);
 if (!open || !changedBy) return;
 let cancelled = false;
 (async () => {
 const { data } = await supabase.from("profiles").select("full_name").eq("user_id", changedBy).maybeSingle();
 if (!cancelled) setReportChangerName((data as { full_name?: string | null } | null)?.full_name ?? null);
 })();
 return () => { cancelled = true; };
 }, [open, changedBy]);

 useEffect(() => {
 if (session && open) {
 fetchObservations();
 setMediaFetchFailed(false);
 fetchPhotoUrl();
 } else {
 setMediaFetchFailed(false);
 setObservations([]);
 setPhotoUrl(null);
 setPhotoUrls([]);
 setAudioUrl(null);
 setSessionSeverity(null);
 setFetchedNotes(null);
 setTriagedBy({ id: null, at: null, name: null });
 setWorkerResolvedAt(null);
 setLocationName(null);
 setAiAnalysis(null);
 setTriage(null);
 setFocusId(null);
 setReportRow(null);
 setFollowRef({ focusId: null, sessionId: null });
 setAnalyzeFailed(false);
 }
 // Always reset lightbox state when session changes or drawer toggles so
 // reopening a session never starts stuck in fullscreen.
 setLightboxIndex(null);
 }, [session, open]);

 // Resultado del intento POR SESIÓN, y sobrevive a cerrar y volver a abrir.
 //
 // Antes era un Set de «ya lo intenté», y al cerrar se limpiaban aiAnalysis y
 // analyzeFailed: al reabrir, el Set impedía relanzar y no quedaba nada en estado,
 // así que la sección de fotos se quedaba EN BLANCO. Guardando el desenlace no hay
 // hueco posible: o hay análisis, o hay «no se reconoció nada», o hay «no se pudo».
 const attemptRef = useRef<Map<string, "failed" | "none">>(new Map());
 const [attempt, setAttempt] = useState<"failed" | "none" | null>(null);
 /* LO PROVISIONAL NO SE GUARDA (A13; F0, 01-10-2026)… y por eso no puede volver a pedirse
    solo. Un veredicto provisional (solo el modelo barato, sin venir seguro) se enseña pero
    no llega a `ai_analysis`: el siguiente `fetchPhotoUrl` —cualquier refresco del parte,
    p. ej. el de tiempo real al subir las fotos— lo dejaba en null, el efecto de abajo veía
    «sin análisis» y lo pedía OTRA VEZ, pagando cada vez (la función tampoco guarda lo
    provisional). Se recuerda aquí por parte, mientras viva la ficha: se sigue enseñando,
    no se relanza solo, y «Reintentar» / «Volver a analizar» lo piden a propósito
    (05-10-2026, plan cultivos SIEX F7; hallazgo S12 del robot). */
 const provisionalRef = useRef<Map<string, AiAnalysisData>>(new Map());

 useEffect(() => {
 if (!open || !session?.id) { setAttempt(null); return; }
 setAttempt(attemptRef.current.get(session.id) ?? null);
 // transcribeFailed no se reseteaba al cambiar de sesión: si fallaba en una,
 // la siguiente se abría con el fallo puesto y su efecto de transcripción
 // salía antes de tiempo, así que no se transcribía nunca. Ahora es por sesión.
 setTranscribeFailed(transcribeFailedRef.current.has(session.id));
 }, [open, session?.id]);

 // Foto del inspector. Tres de los cuatro sitios que abren este cajón se montan el
 // objeto `session` a mano y solo le ponen el NOMBRE, así que la foto no llegaba y
 // salían las iniciales aunque la persona la tuviera puesta — al administrador el
 // primero, porque su ficha se abre justo desde esos sitios. En vez de arreglarlo en
 // cada llamador (y volver a olvidarlo en el siguiente que se añada) se rellena aquí:
 // una fila de `profiles` por `inspector_id`, y solo cuando no ha venido ya.
 //
 // Las políticas de `profiles` dan de sí: administrador, encargado y perito ven las
 // fichas de su empresa, y el trabajador la suya —que es la única que puede tocarle,
 // porque el inspector de su sesión es él mismo.
 const [inspectorAvatarUrl, setInspectorAvatarUrl] = useState<string | null>(null);
 useEffect(() => {
 setInspectorAvatarUrl(null);
 if (!open) return;
 const inspectorId = session?.inspector_id;
 if (!inspectorId || session?.inspector?.avatar_url) return;
 let cancelled = false;
 (async () => {
 const { data } = await supabase
 .from("profiles")
 .select("avatar_url")
 .eq("user_id", inspectorId)
 .maybeSingle();
 if (!cancelled) setInspectorAvatarUrl(data?.avatar_url ?? null);
 })();
 return () => { cancelled = true; };
 }, [open, session?.inspector_id, session?.inspector?.avatar_url]);

 // Se analiza una vez por sesión: si ya hay resultado guardado (incluido el «no se
 // reconoció nada», que también se persiste) no se vuelve a llamar nunca.
 useEffect(() => {
 if (!open || !session?.id || loading) return;
 if (aiAnalysis || analyzeBusy) return;
 if (attemptRef.current.has(session.id)) return;
 if (provisionalRef.current.has(session.id)) return;
 const hasPhotos = photoUrls.length > 0 || !!photoUrl;
 if (!hasPhotos || isWorker) return;
 handleAnalyzeWithAI();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [open, session?.id, loading, aiAnalysis, analyzeBusy, photoUrls, photoUrl, isWorker]);

 const handleTranscribeVoice = async () => {
 if (!session || !audioUrl) return;
 // Reintentar a mano borra la marca de fallo; si no, el efecto seguiría
 // creyendo que esta sesión ya está descartada.
 transcribeFailedRef.current.delete(session.id);
 setTranscribeBusy(true);
 setTranscribeFailed(false);
 try {
 const { data, error } = await supabase.functions.invoke(
"transcribe-scouting-voice",
 { body: { session_id: session.id, target_lang: i18n.language } },
 );
 if (error) throw error;
 setVoiceTranscript(data?.transcript || null);
 setVoiceTranscriptLang(data?.transcript_lang || null);
 setVoiceTranslations(data?.translations || {});
 } catch (e) {
 console.warn("transcribe-scouting-voice failed:", e);
 setTranscribeFailed(true);
 // Que no se relance solo al reabrir; se reintenta desde el botón.
 if (session?.id) transcribeFailedRef.current.add(session.id);
 } finally {
 setTranscribeBusy(false);
 }
 };

 // Se transcribe una vez. Cuando sale bien, la función edge guarda el texto en
 // voice_transcript, así que al reabrir ya viene de la base de datos y esto no
 // vuelve a dispararse. Cuando FALLA no se guarda nada, y antes eso significaba
 // reintentarlo en cada apertura: mismo problema que tenían las fotos. El ref
 // recuerda el fallo por sesión y la UI ofrece «Reintentar».
 const transcribeFailedRef = useRef<Set<string>>(new Set());
 useEffect(() => {
 if (!open || !session || isWorker) return;
 if (!audioUrl) return;
 if (voiceTranscript || transcribeBusy || transcribeFailed) return;
 if (transcribeFailedRef.current.has(session.id)) return;
 handleTranscribeVoice();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [open, session?.id, audioUrl, voiceTranscript]);

 // Translate to manager's language on-demand if missing
 useEffect(() => {
 if (!open || !session || isWorker) return;
 if (!voiceTranscript || !voiceTranscriptLang) return;
 const target = i18n.language;
 if (voiceTranscriptLang === target) return;
 if (voiceTranslations[target]) return;
 if (transcribeBusy) return;
 handleTranscribeVoice();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [open, session?.id, voiceTranscript, voiceTranscriptLang, i18n.language]);


 const fetchPhotoUrl = async () => {
 if (!session) return;
 const { data, error } = await supabase
 .from("pest_scouting_sessions")
 .select("photo_url, photo_urls, audio_url, source, voice_transcript, voice_transcript_lang, voice_translations, severity, notes, triaged_by, triaged_at, worker_resolved_at, review_status, ai_analysis, linked_task_id, triage_status, triage_pest_id, triage_pest_name_snapshot, dismissal_reason, dismissal_note, focus_id, inspector_id, followup_answer, followup_skip_reason, followup_focus_id, followup_session_id, evolution_verdict, evolution_verdict_by, greenhouse:greenhouses(name, location:locations(name))")
 .eq("id", session.id)
 .single();
 if (error) {
 // Sin esto, un fallo dejaría el hueco reservado puesto para siempre.
 setMediaFetchFailed(true);
 return;
 }
 const d = data as any;
 setPhotoUrl(d?.photo_url || null);
 setPhotoUrls(Array.isArray(d?.photo_urls) ? d.photo_urls : []);
 setAudioUrl(d?.audio_url || null);
 setManualMap(d?.source === "manual_map");
 setVoiceTranscript(d?.voice_transcript || null);
 setVoiceTranscriptLang(d?.voice_transcript_lang || null);
 setVoiceTranslations((d?.voice_translations as Record<string, string>) || {});
 setSessionSeverity(d?.severity || null);
 setFetchedNotes(d?.notes ?? null);
 setTriagedBy({ id: d?.triaged_by ?? null, at: d?.triaged_at ?? null, name: null });
 setWorkerResolvedAt(d?.worker_resolved_at || null);
 setLocationName(d?.greenhouse?.location?.name || null);
 // Lo guardado manda; si no hay, lo provisional que ya se enseñó en esta ficha (no se pierde ni se repide).
 setAiAnalysis((d?.ai_analysis as AiAnalysisData) || provisionalRef.current.get(session.id) || null);
 setFocusId(d?.focus_id || null);
 setFollowRef({ focusId: (d as { followup_focus_id?: string | null })?.followup_focus_id ?? null, sessionId: (d as { followup_session_id?: string | null })?.followup_session_id ?? null });
 setReportRow({
 inspector_id: d?.inspector_id ?? null,
 followup_answer: d?.followup_answer ?? null,
 followup_skip_reason: d?.followup_skip_reason ?? null,
 evolution_verdict: d?.evolution_verdict ?? null,
 evolution_verdict_by: d?.evolution_verdict_by ?? null,
 });
 setTriage({
 triage_status: d?.triage_status ||"pending_triage",
 triage_pest_id: d?.triage_pest_id || null,
 triage_pest_name_snapshot: d?.triage_pest_name_snapshot || null,
 dismissal_reason: d?.dismissal_reason || null,
 dismissal_note: d?.dismissal_note || null,
 worker_resolved_at: d?.worker_resolved_at || null,
 review_status: (d as any)?.review_status || null,
 linked_task_id: d?.linked_task_id || null,
 });
 };

 const fetchObservations = async () => {
 if (!session) return;
 
 setLoading(true);
 try {
 const { data, error } = await supabase
 .from("pest_scouting_observations")
 .select(`
 id,
 pest_id,
 count,
 unit,
 severity,
 notes,
 pest_name_snapshot,
 observed_row_start,
 observed_row_end,
 pest:pests(name, type, translation_key)
 `)
 .eq("scouting_session_id", session.id);

 if (error) {
 console.error("Error fetching observations:", error);
 return;
 }

 setObservations(data || []);
 } finally {
 setLoading(false);
 }
 };

 const getSeverityColor = (severity: string | null) => {
 switch (severity) {
 case"low": return PILL_COLORS.blue;
 case"medium": return PILL_COLORS.amber;
 case"high": return PILL_COLORS.red;
 case"critical": return PILL_COLORS.red;
 default: return PILL_COLORS.slate;
 }
 };

 const getTypeIcon = (type: string | null) => {
 switch (type) {
 case"insect": return"🐛";
 case"fungus": return"🍄";
 case"bacteria": return"🦠";
 case"virus": return"🔬";
 default: return"🐜";
 }
 };

 const softDismissOnFkViolation = async (): Promise<boolean> => {
 if (!session) return false;
 const { error } = await supabase
 .from("pest_scouting_sessions")
 .update({
 triage_status:"dismissed",
 dismissal_reason:"linked_records",
 dismissal_note: null,
 triaged_by: user?.id ?? null,
 triaged_at: new Date().toISOString(),
 } as any)
 .eq("id", session.id);
 if (error) {
 console.error("Soft-dismiss after FK violation failed:", error);
 return false;
 }
 return true;
 };

 const handleDeleteSession = async () => {
 if (!session) return;

 setIsDeleting(true);
 try {
 // Try to clear non-dismissed alerts and unlink dismissed ones so the
 // hard delete below can succeed. Errors here are non-fatal — we still
 // attempt the session delete and fall back to soft-dismiss on FK error.
 await supabase
 .from("spray_alerts")
 .delete()
 .eq("linked_session_id", session.id)
 .eq("is_dismissed", false);
 await supabase
 .from("spray_alerts")
 .update({ linked_session_id: null })
 .eq("linked_session_id", session.id)
 .eq("is_dismissed", true);

 await supabase
 .from("pest_scouting_observations")
 .delete()
 .eq("scouting_session_id", session.id);

 const { error: sessionError } = await supabase
 .from("pest_scouting_sessions")
 .delete()
 .eq("id", session.id);

 if (sessionError) {
 // Postgres FK violation — session is referenced by tasks / spray
 // alerts we can't safely remove. Soft-dismiss instead.
 if ((sessionError as any).code ==="23503") {
 const ok = await softDismissOnFkViolation();
 if (ok) {
 toast({ title: t("pests.scouting.dismissedBecauseLinked") });
 onOpenChange(false);
 invalidateScoutingData(queryClient);
 onSessionDeleted?.();
 return;
 }
 }
 console.error("Error deleting session:", sessionError);
 toast({ title: t("common.error"), variant:"destructive" });
 return;
 }

 toast({ title: t("pests.scouting.sessionDeleted") });
 onOpenChange(false);
 invalidateScoutingData(queryClient);
 onSessionDeleted?.();
 } finally {
 setIsDeleting(false);
 setShowDeleteConfirm(false);
 }
 };


 const handleDeleteSingleObservation = async () => {
 if (!deletingObservation) return;
 setIsDeletingObservation(true);
 try {
 const { error } = await supabase
 .from("pest_scouting_observations")
 .delete()
 .eq("id", deletingObservation.id);
 if (error) {
 toast({ title: t("common.error"), variant:"destructive" });
 return;
 }
 toast({ title: t("pests.scouting.observationDeleted") });
 setDeletingObservation(null);
 fetchObservations();
 invalidateScoutingData(queryClient);
 onSessionUpdated?.();
 } finally {
 setIsDeletingObservation(false);
 }
 };

 /** Guarda el resultado en la sesión. No es crítico: si falla, el diagnóstico ya
  *  está en pantalla; solo significa que la próxima apertura repetirá la llamada.
  *  Un veredicto PROVISIONAL (solo el modelo barato, sin venir seguro) se enseña pero
  *  no se guarda: guardarlo lo congelaría como definitivo en el parte (A13; 01-10-2026,
  *  plan cultivos SIEX, F0). Un fallo ni siquiera llega aquí: `pedirAnalisis` lanza. */
 const persistAnalysis = async (result: AiAnalysisData) => {
 if (!session?.id) return;
 if (!sePuedeGuardarEnElParte(result)) return;
 try {
 await supabase
 .from("pest_scouting_sessions")
 .update({ ai_analysis: result } as any)
 .eq("id", session.id);
 } catch (e) {
 console.warn("Failed to persist ai_analysis:", e);
 }
 };

 const handleAnalyzeWithAI = async (forzar = false) => {
 // TODAS las fotos de la sesión van en la misma llamada: son de la misma
 // incidencia, así que el veredicto tiene que salir de verlas juntas. Antes se
 // mandaba solo photoUrl y su resultado se estampaba encima de las tres.
 const photosToAnalyze = photoUrls.length > 0 ? photoUrls : photoUrl ? [photoUrl] : [];
 if (photosToAnalyze.length === 0) return;
 if (session?.id) attemptRef.current.delete(session.id);
 if (session?.id) provisionalRef.current.delete(session.id);
 setAttempt(null);
 setAnalyzeResult(null);
 setAnalyzeFailed(false);
 setAnalyzeDone(false);
 setAnalyzeRevealed(false);
 // Si ya iba de camino desde el formulario, la barra sigue desde donde iba, no desde 0.
 setAnalyzeSince((!forzar && session?.id ? inicioDelAnalisis(session.id) : null) ?? Date.now());
 setAnalyzeBusy(true);
 const lang = idiomaDelAnalisis(i18n.language);
 /* Se manda el bloque cuando se conoce: la función lo usa para leer el cultivo,
    el tiempo de esa finca y el histórico reciente antes de diagnosticar.
    `session_id`: ata el análisis guardado a este parte, para que se borre con él.
    `force`: solo repite (y vuelve a pagar) cuando un administrador lo pide sobre un
    diagnóstico que ya existe; el servidor lo ignora para el resto de roles. */
 const desdeLasFotos = async () => {
 const base64s = await Promise.all(
 photosToAnalyze.slice(0, MAX_FOTOS_ANALISIS).map(async (u) => {
 const resp = await fetch(u);
 const blob = await resp.blob();
 const file = new File([blob],"scouting.jpg", { type: blob.type ||"image/jpeg" });
 return fileToBase64(file);
 }),
 );
 return pedirAnalisis({ base64s, lang, blockId: session?.block_id ?? null, sessionId: session?.id ?? null, force: forzar });
 };
 try {
 /* Recién enviado desde aquí mismo (28-09-2026): el análisis ya va de camino desde el
    formulario. Se espera a ESE —no se paga otro— y si falla, se pide como siempre. */
 const enCamino = !forzar && session?.id ? analisisEnMarcha(session.id) : null;
 const empezo = Date.now();
 const result: AiAnalysisData = enCamino ? await enCamino.catch(() => desdeLasFotos()) : await desdeLasFotos();
 /* La barra llega al 100 y se queda un momento antes de dar paso al diagnóstico (28-09-2026).
    Si ya estaba hecho (contesta al momento), sin barra que enseñar: directo. */
 if (Date.now() - empezo > 600) {
 setAnalyzeDone(true);
 await new Promise((r) => setTimeout(r, 500));
 setAnalyzeRevealed(true);
 }
 // Provisional: se enseña y se recuerda, pero ni se guarda ni se vuelve a pedir solo.
 if (session?.id && !sePuedeGuardarEnElParte(result)) provisionalRef.current.set(session.id, result);
 if (!result.identified) {
 // La IA respondió y NO reconoció nada. Es un resultado, no un fallo: se
 // guarda para no repetir la llamada en cada apertura (salvo que sea provisional:
 // persistAnalysis lo descarta). Si NINGÚN modelo contestó, `pedirAnalisis` lanza
 // y se va al catch de abajo: antes eso acababa aquí, guardado como «nada».
 setAiAnalysis(result);
 if (session?.id) attemptRef.current.set(session.id,"none");
 setAttempt("none");
 await persistAnalysis(result);
 return;
 }
 setAnalyzeResult(result);
 setAiAnalysis(result);
 // Se guarda SIEMPRE, también si la confianza es baja. Antes solo se guardaba
 // por encima de 0.6 y el resto se quedaba sin rastro, así que al reabrir la
 // ficha se volvía a llamar a la IA una y otra vez. Lo que decide si el
 // diagnóstico se ENSEÑA es hasUsableAnalysis, no el hecho de guardarlo.
 await persistAnalysis(result);
 } catch (err) {
 // Fallo de verdad (red, función caída, 4xx…, o ningún modelo dio veredicto:
 // `AnalisisSinVeredicto`, 01-10-2026). NO se persiste: no es un
 // veredicto de la IA y tiene que poder reintentarse. Se marca en el ref para
 // que no se relance solo al reabrir, y la UI ofrece «Reintentar».
 console.error("Analyze with AI failed:", err);
 setAnalyzeFailed(true);
 if (session?.id) attemptRef.current.set(session.id,"failed");
 setAttempt("failed");
 } finally {
 setAnalyzeBusy(false);
 }
 };

 /* TODAVÍA NO HA LLEGADO: se abre la hoja igual, con el esqueleto dentro.
    Abrirla y rellenarla después es lo que se leía como «se abre mientras
    carga»; no abrirla hasta tenerlo todo (lo de antes) se leía como que el
    toque no había hecho nada. El esqueleto dice las dos cosas a la vez: te he
    oído, y esto es lo que va a caber (29-08-2026). */
 if (!session) {
 if (!sessionLoading) return null;
 return (
 <Drawer open={open} onOpenChange={onOpenChange}>
 <DrawerContent fit="short" className={"bg-white flex flex-col " + (heightClassName ?? "")}>
 <DrawerHeader className="border-b pb-3 pt-3 px-4 flex-shrink-0">
 <div className="flex items-center justify-between gap-2">
 <DrawerTitle className="flex items-center gap-2 text-[1.2rem] sm:text-[1.35rem] min-w-0">
 <Bug className="w-5 h-5 text-black shrink-0" />
 <span className="truncate">{t("teams.sessionDetails")}</span>
 </DrawerTitle>
 <DrawerClose className={SHEET_CLOSE_CLASS} aria-label={t("common.close")}>
 <SheetCloseIcon />
 </DrawerClose>
 </div>
 </DrawerHeader>
 {/* Las medidas son las del contenido de verdad —la tarjeta de datos, la
     nota de voz, la tira de fotos—, para que al llegar no salte nada. */}
 <div aria-hidden className="flex-1 min-h-0 overflow-hidden p-4 sm:p-6 space-y-4">
 <Skeleton className="h-[188px] w-full rounded-2xl" />
 <Skeleton className="h-[104px] w-full rounded-2xl" />
 <Skeleton className="h-4 w-24 rounded-full" />
 <Skeleton className="h-[126px] w-[126px] rounded-xl" />
 </div>
 </DrawerContent>
 </Drawer>
 );
 }

 return (
 <>
 <Drawer open={open} onOpenChange={onOpenChange}>
 <DrawerContent
 /* Ficha de detalle: se AJUSTA a su contenido en el teléfono. Una sesión sin
    fotos ni nota abre corta; una con mucho contenido crece hasta arriba y hace
    scroll dentro. Antes iba `expanded` = pantalla completa FIJA, y una sesión
    vacía dejaba un hueco enorme abajo — justo lo que ya se quitó al eliminar el
    viejo sm:min-h-[70vh]; `auto` es lo que de verdad lo cumple. El max-h de la
    carcasa (72dvh) sigue siendo el tope antes de estirarse. */
 fit={heightClassName ? "short" : "auto"}
 className={"bg-white flex flex-col " + (heightClassName ?? "")}
 onInteractOutside={(e) => { if (lightboxIndex !== null) e.preventDefault(); }}
 onPointerDownOutside={(e) => { if (lightboxIndex !== null) e.preventDefault(); }}
 >
 <DrawerHeader className="border-b pb-3 pt-3 px-4 flex-shrink-0">
 <div className="flex items-center justify-between gap-2">
 <DrawerTitle className="flex items-center gap-2 text-[1.2rem] sm:text-[1.35rem] min-w-0">
 <Bug className="w-5 h-5 text-black shrink-0" />
 <span className="truncate">{t("teams.sessionDetails")}</span>
 {/* La pastilla de gravedad se retiró de la cabecera. */}
 {/* Apuntado a mano desde el mapa (22-09-2026): pastilla «Manual». */}
 {manualMap && (
 <Badge variant="outline" className="text-xs shrink-0 border-teal-300 bg-teal-50 text-teal-900 inline-flex items-center gap-1" data-testid="session-manual-badge">
 <PencilLine className="w-3 h-3" />{t("healthMap.manual.badge")}
 </Badge>
 )}
 </DrawerTitle>
 <div className="flex items-center gap-1 shrink-0">
 {/* La X compartida de ui/sheetChrome, no un Button ghost cuadrado. */}
 <DrawerClose className={SHEET_CLOSE_CLASS} aria-label={t("common.close")}>
 <SheetCloseIcon />
 </DrawerClose>
 </div>
 </div>
 </DrawerHeader>

 <div data-sheet-scroll className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
 <div className="p-4 sm:p-6 space-y-4">
 {/* Consolidated session info card */}
 {(() => {
 const greenhouseName = session.greenhouse?.name || (session as any).greenhouse_name_snapshot || null;
 const blockName = session.block?.name || (session as any).block_name_snapshot || null;
 // Extract Línea/Line/Linea from notes
 const rawNotes = session.notes || fetchedNotes ||"";
 const lineMatch = rawNotes.match(/^(?:l[ií]nea|line)\s*:\s*(\d+)\s*$/i);
 const lineNum = lineMatch ? lineMatch[1] : null;
 const cleanedNotes = lineMatch ?"" : rawNotes.trim();
 const locationParts = [locationName, greenhouseName, blockName, lineNum ? `${t("worker.incident.line")} ${lineNum}` : null].filter(Boolean);

 return (
 <div className="rounded-2xl bg-white border border-[#b6dded] shadow-sm divide-y divide-[#e7f1f7]">
 <div className="flex items-start gap-3 p-3">
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: TILE_BLUE, boxShadow: taskTypeTileShadow(TILE_BLUE) }}
 >
 <Calendar className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("common.date")}</p>
 <p className="font-semibold text-sm">
 {formatDateLong(new Date(session.date))}
 {session.created_at && (
 <span className="text-muted-foreground font-semibold"> · {formatDate(new Date(session.created_at),"HH:mm")}</span>
 )}
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3 p-3">
 {/* Foto del inspector (o iniciales). Mismo aspecto en toda la app. */}
 <PersonAvatar
 name={session.inspector?.full_name}
 avatarUrl={session.inspector?.avatar_url ?? inspectorAvatarUrl}
 />
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("teams.inspectedBy")}</p>
 <p className="font-semibold text-sm truncate">
 {session.inspector?.full_name || t("teams.unknownName")}
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3 p-3">
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: TILE_GREEN, boxShadow: taskTypeTileShadow(TILE_GREEN) }}
 >
 <House className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("common.location")}</p>
 <p className="font-semibold text-sm break-words">
 {locationParts.length > 0 ? locationParts.join(" ·") :"—"}
 </p>
 </div>
 </div>
 {/* A qué foco pertenece (24-09-2026). El color de la tesela es el del
     estado del foco en el mapa, y el botón lleva a él. */}
 {mediaReady && focusInfo && (() => {
 const ink = LINE_STATE_INK[focusInfo.state];
 return (
 /* En el teléfono el botón baja debajo del texto (alineado con él): a su
    lado partía «Foco n.º N · plaga» en cuatro renglones. */
 <div className="flex flex-wrap items-start gap-x-3 gap-y-2 p-3" data-testid="session-focus" data-focus-id={focusInfo.id} data-state={focusInfo.state}>
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: ink, boxShadow: `0 6px 14px color-mix(in srgb, ${ink} 26%, transparent), inset 0 1px 0 rgba(255,255,255,0.28)` }}
 >
 <Crosshair className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-0">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pests.scouting.sessionFocus.label")}</p>
 <p className="font-semibold text-sm break-words">
 {t("healthMap.focus.number", { n: focusInfo.focusNumber })} · {focusInfo.pestName}
 </p>
 <p className="text-xs text-muted-foreground">
 {t(`healthMap.state.${focusInfo.state}`)} · {t("healthMap.focus.samplings", { n: focusInfo.samplings })}
 </p>
 </div>
 {focusLink !== "none" && (
 <Button
 size="sm"
 onClick={openFocus}
 data-testid="session-focus-open"
 data-link={focusLink}
 className="ml-12 sm:ml-0 sm:self-center shrink-0 bg-white text-black border border-[#b6dded] shadow-sm hover:bg-slate-50"
 >
 <MapPin className="w-3.5 h-3.5 mr-1" />
 {focusLink === "here" ? t("pests.scouting.sessionFocus.open") : t("pests.scouting.sessionFocus.viewOnMap")}
 </Button>
 )}
 </div>
 );
 })()}
 {/* Gravedad REGISTRADA y estado del parte (auditoría M29 · T06-04): el
     perito solo veía la gravedad que adivina la IA, que a veces dice otra
     cosa, y en un descartado no veía ni «Descartado» ni el motivo. */}
 {mediaReady && triage && !isWorker && (() => {
 const status = sessionStatusOf(triage);
 const sevColor = sessionSeverity ? SEVERITY_COLORS[sessionSeverity as keyof typeof SEVERITY_COLORS] : undefined;
 const statusLabel =
 status === "resolved" ? t("pests.scouting.statusResolved")
 : status === "dismissed" ? t("pests.scouting.statusDismissed")
 : status === "in_treatment" ? t("pests.scouting.statusInTreatment")
 : status === "classified" ? t("pests.scouting.statusClassified")
 : t("pests.scouting.triage.pendingBadge");
 const reason = status === "dismissed" && triage.dismissal_reason
 ? t(`pests.scouting.triage.reasons.${triage.dismissal_reason}`, triage.dismissal_reason)
 : null;
 const byLine = triagedBy.id && status !== "pending"
 ? [
 t("pests.scouting.detail.by", { name: triagedBy.name || t("teams.unknownName") }),
 triagedBy.at ? formatDate(new Date(triagedBy.at), "PPp") : null,
 ].filter(Boolean).join(" · ")
 : null;
 return (
 <>
 <div className="flex items-start gap-3 p-3" data-testid="session-severity" data-severity={sessionSeverity ?? ""}>
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: sevColor ?? TILE_NEUTRAL, boxShadow: taskTypeTileShadow(sevColor ?? TILE_NEUTRAL) }}
 >
 <Bug className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pests.scouting.detail.severityLabel")}</p>
 <p className="font-semibold text-sm">
 {sessionSeverity ? t(`dashboard.severity.${sessionSeverity}`, sessionSeverity) : t("pests.scouting.detail.noSeverity")}
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3 p-3" data-testid="session-status" data-status={status}>
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: TILE_NEUTRAL, boxShadow: taskTypeTileShadow(TILE_NEUTRAL) }}
 >
 <CheckCircle2 className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pests.scouting.detail.statusLabel")}</p>
 <p className="font-semibold text-sm break-words">{reason ? `${statusLabel} · ${reason}` : statusLabel}</p>
 {byLine && <p className="text-xs text-muted-foreground break-words" data-testid="session-triaged-by">{byLine}</p>}
 </div>
 </div>
 </>
 );
 })()}
 {/* Cómo está, según quien muestreó (D19): al clasificar se veía y luego
     desaparecía. «No lo sé» también cuenta (ver `sessionReport`). */}
 {mediaReady && report && !isWorker && (() => {
 const verdict = reportVerdict(report);
 const tile = verdict ? VERDICT_TILE[verdict] : { color: TILE_NEUTRAL, Icon: HelpCircle };
 const TileIcon = tile.Icon;
 const rawName = session.inspector?.full_name || t("teams.unknownName");
 // «Según ronaldo» con minúscula quedaba raro: la primera letra, en mayúscula.
 const inspectorName = rawName.charAt(0).toLocaleUpperCase() + rawName.slice(1);
 /* «se avisó en la línea 18 hace 3 días (araña roja)»: con qué se comparó (27-09-2026). */
 const refText = (() => {
 if (!refInfo) return null;
 const where = refInfo.line != null ? t("pests.scouting.report.ref.atLine", { line: refInfo.line }) : t("pests.scouting.report.ref.here");
 let when = "";
 if (refInfo.date) {
 const days = Math.round((Date.parse(todayIso() + "T00:00:00Z") - Date.parse(refInfo.date + "T00:00:00Z")) / 86_400_000);
 when = days <= 0 ? t("pests.scouting.report.ref.today") : days === 1 ? t("pests.scouting.report.ref.yesterday") : t("pests.scouting.report.ref.daysAgo", { days });
 }
 const pest = refInfo.pestName ? ` (${refInfo.pestName})` : "";
 return { where, when, pest };
 })();
 // El inspector de la base: no todas las pantallas que abren la ficha lo pasan.
 const saidBy = !!user?.id && (reportRow?.inspector_id ?? session.inspector_id) === user.id
 ? t("pests.scouting.report.saidByYou")
 : t("pests.scouting.report.saidBy", { name: inspectorName });
 let value: string;
 let detail: string;
 if (report.kind === "not_asked") {
 value = t("pests.scouting.report.notAsked");
 detail = t(`pests.scouting.focusLink.skip.${report.reason}`);
 } else if (verdict === "unknown") {
 value = t("pests.scouting.report.unknownVerdict");
 detail = saidBy;
 } else if (verdict) {
 value = t(`healthMap.focus.evolution.${verdict}`);
 detail = report.changedBy
 ? t("pests.scouting.report.changedBy", { name: reportChangerName || t("teams.unknownName") })
 : saidBy;
 } else if (report.kind === "answer" && report.answer === "different") {
 value = t("pests.scouting.report.different");
 detail = refText
 ? t("pests.scouting.report.differentRef", { name: inspectorName, ...refText })
 : t("pests.scouting.report.differentDetail", { name: inspectorName });
 } else {
 value = t("pests.scouting.report.unknownSame");
 detail = refText ? `${saidBy} · ${t("pests.scouting.report.ref.seen", refText)}` : saidBy;
 }
 return (
 <div
 className="flex items-start gap-3 p-3"
 data-testid="session-report"
 data-kind={report.kind}
 data-verdict={verdict ?? ""}
 data-answer={report.kind === "answer" ? report.answer : ""}
 >
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: tile.color, boxShadow: taskTypeTileShadow(tile.color) }}
 >
 <TileIcon className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pests.scouting.report.label")}</p>
 <p className="font-semibold text-sm break-words">{value}</p>
 <p className="text-xs text-muted-foreground break-words">{detail}</p>
 </div>
 </div>
 );
 })()}
 {/* La gravedad ya no va aquí: está en la cabecera, junto al título. */}
 {cleanedNotes && (
 <div className="flex items-start gap-3 p-3">
 <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
 <FileText className="w-4 h-4 text-primary" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("common.notes")}</p>
 <p className="text-sm text-foreground whitespace-pre-wrap break-words">{cleanedNotes}</p>
 </div>
 </div>
 )}
 </div>
 );
 })()}

 {/* El parte solo: sus dos acciones, sin la palabra «foco», debajo de la tarjeta de datos. */}
 {mediaReady && canActSolo && soloFocus && soloFocus.state !== "resolved" && (
 <div className="grid grid-cols-2 gap-2" data-testid="session-solo-actions">
 <Button className="h-auto min-h-11 whitespace-normal bg-emerald-600 px-3 py-2 leading-tight hover:bg-emerald-700" onClick={() => setSoloUpdateOpen(true)} data-testid="session-action-update">
 {t("healthMap.focus.actions.addUpdate")}
 </Button>
 <Button variant="outline" className="h-auto min-h-11 whitespace-normal px-3 py-2 leading-tight" onClick={() => setSoloFollowOpen(true)} data-testid="session-action-followup">
 {t("healthMap.focus.actions.followUp")}
 </Button>
 </div>
 )}

 {/* Worker-resolved banner */}
 {settled && workerResolvedAt && (
 <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800">
 <CheckCircle2 className="w-5 h-5 shrink-0" />
 <div className="text-sm">
 <p className="font-semibold">{t("worker.incident.bannerWorkerResolvedTitle")}</p>
 <p className="text-xs opacity-80">{t("worker.incident.bannerWorkerResolvedDesc")}</p>
 </div>
 </div>
 )}

 {/* Hueco reservado mientras llega la segunda consulta: mismo alto
 aproximado que la tarjeta de audio más la rejilla de miniaturas. */}
 {!mediaReady && (
 <div aria-hidden className="space-y-3">
 <Skeleton className="h-[68px] w-full rounded-2xl" />
 <Skeleton className="h-[126px] w-[126px] rounded-xl" />
 </div>
 )}

 {/* Audio Section */}
 {mediaReady && audioUrl && (
 <div className="rounded-2xl border border-[#b6dded] bg-white shadow-sm p-3 space-y-2">
 {/* Todo en una fila, también en el móvil: el reproductor mínimo mide lo que
 mide y no arrastra la anchura mínima del <audio> nativo, que era lo que
 antes obligaba a apilarlo debajo del título. */}
 <div className="flex items-center gap-2">
 <div
 className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
 style={{ backgroundColor: TILE_VIOLET, boxShadow: taskTypeTileShadow(TILE_VIOLET) }}
 >
 <Mic className="w-4 h-4 text-white" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-sm font-semibold text-foreground leading-tight truncate">{t("pests.scouting.voiceNoteTitle","Nota de voz")}</p>
 <p className="text-[11px] text-muted-foreground leading-tight truncate">{t("pests.scouting.voiceNoteSubtitle","Transcripción automática")}</p>
 </div>
 <MiniAudioPlayer src={audioUrl} />
 </div>
 {!isWorker && (
 <div className="pt-2 border-t border-[#b6dded] space-y-2">
 {transcribeBusy && !voiceTranscript && (
 <div className="flex items-center gap-2 text-xs text-muted-foreground">
 <Loader2 className="w-3 h-3 animate-spin" />
 {t("pests.scouting.transcribing")}
 </div>
 )}
 {transcribeFailed && !voiceTranscript && (
 <Button
 size="sm"
 onClick={handleTranscribeVoice}
 disabled={transcribeBusy}
 className="bg-white text-black border border-[#b6dded] shadow-sm hover:bg-slate-50"
 >
 {t("pests.scouting.transcribeRetry")}
 </Button>
 )}
 {voiceTranscript && (() => {
 const target = i18n.language;
 const translated = voiceTranslations?.[target];
 const differentLang =
 voiceTranscriptLang && voiceTranscriptLang !== target;
 const showOriginal = !differentLang || showOriginalTranscript || !translated;
 const body = showOriginal ? voiceTranscript : translated;
 return (
 <div className="space-y-1">
 <div className="flex items-center justify-between">
 <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
 {showOriginal
 ? t("pests.scouting.transcriptOriginal")
 : t("pests.scouting.transcriptTranslated")}
 {voiceTranscriptLang && (
 <span className="ml-1 opacity-60">
 ({(showOriginal ? voiceTranscriptLang : target).toUpperCase()})
 </span>
 )}
 </p>
 {differentLang && translated && (
 <button
 type="button"
 className="text-[11px] text-black underline"
 onClick={() => setShowOriginalTranscript((v) => !v)}
 >
 {showOriginal
 ? t("pests.scouting.showTranslation")
 : t("pests.scouting.showOriginal")}
 </button>
 )}
 </div>
 <p className="text-sm text-foreground whitespace-pre-wrap">
 {body}
 </p>
 </div>
 );
 })()}
 </div>
 )}
 </div>
 )}

 {/* Photos Section — small square thumbnails. Tapping opens an
 in-app lightbox (never the raw Supabase storage URL). */}
 {mediaReady && (() => {
 const allPhotos = photoUrls.length > 0 ? photoUrls : photoUrl ? [photoUrl] : [];
 if (allPhotos.length === 0) return null;
 // La rejilla se ajusta al número de fotos en vez de ser siempre de 3: con una
 // sola foto quedaban dos columnas vacías y una miniatura de 82 px perdida al
 // lado del texto. Una foto → 168 px, dos → 116 px, tres o más → los 82 px de
 // siempre. El ancho total nunca pasa de 264 px, así que el diagnóstico de al
 // lado conserva su sitio.
 // Un 25 % más pequeñas que el tamaño original en TODOS los dispositivos (168→126,
 // 240→180, 264→198), así que ya no hace falta partirlo por breakpoint. Sigue
 // ajustándose al número de fotos: con una sola no tiene sentido una rejilla de tres.
 const photoGridClass =
 allPhotos.length === 1 ?"grid-cols-1 max-w-[126px]"
 : allPhotos.length === 2 ?"grid-cols-2 max-w-[180px]"
 :"grid-cols-3 max-w-[198px]";
 const thumbs = allPhotos.map((url, i) => (
 <button
 type="button"
 key={i}
 onClick={() => setLightboxIndex(i)}
 className="relative aspect-square rounded-xl overflow-hidden border border-[#b6dded] bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary"
 >
 <ImgOffline
 src={url}
 alt={`Scouting photo ${i + 1}`}
 className="w-full h-full object-cover"
 />
 </button>
 ));
 // Dos colocaciones de las mismas miniaturas. Con diagnóstico van flotando dentro
 // de la tarjeta para que el texto las rodee; en los estados cortos —analizando,
 // no se pudo, no se reconoció nada— no hay nada que rodear, así que se quedan
 // en dos columnas. Con VARIAS fotos solo flotan en md+ (en el móvil ocupan
 // demasiado y no queda ancho para el texto). Pero con UNA sola foto (126 px)
 // sí flota también en el móvil: sobra sitio para el diagnóstico a su derecha, y
 // el texto largo del análisis expandido la rodea por abajo en vez de apilarse.
 const floatClass = allPhotos.length === 1 ?"float-left mr-3" :"md:float-left md:mr-3";
 const floatingThumbs = (
 <div className={`grid gap-2 mb-2 ${floatClass} ${photoGridClass}`}>{thumbs}</div>
 );
 // Con UNA sola foto (126 px) el texto corto —analizando · no se pudo · no se
 // reconoció nada— cabe A SU DERECHA hasta en el móvil, así que va en fila desde
 // el primer breakpoint en vez de apilarse debajo. Con varias, se apila en el
 // móvil (no queda ancho) y solo se pone al lado en md+.
 const besideDir =
 allPhotos.length === 1
 ?"flex-row items-start"
 :"flex-col md:flex-row md:items-start";
 const besideThumbs = (content: React.ReactNode) => (
 <div className={`flex gap-3 ${besideDir}`}>
 <div className={`grid gap-2 shrink-0 ${photoGridClass}`}>{thumbs}</div>
 <div className="flex-1 min-w-0">{content}</div>
 </div>
 );
 return (
 <div className="space-y-3">
 <div className="flex items-center gap-2">
 <Camera className="w-4 h-4 text-black" />
 <p className="text-sm font-semibold">
 {t("pests.scouting.photosTitle","Fotos")}
 {hasUsableAnalysis(aiAnalysis) && (
 <span className="text-muted-foreground font-semibold"> · {t("pests.scouting.aiAutoLabel","análisis automático")}</span>
 )}
 </p>
 </div>
 {/* Cuatro estados y ninguno vacío: analizando · resultado · la IA no
 reconoció nada · no se pudo analizar (con reintento). El caso «no se
 pudo» es el único con botón: repetir un veredicto negativo daría lo
 mismo, pero repetir una llamada que falló tiene todo el sentido. */}
 {analyzeBusy ? (
 besideThumbs(
 <AiAnalysisProgress since={analyzeSince} done={analyzeDone} />,
 )
 ) : hasUsableAnalysis(aiAnalysis) ? (
 <div className={analyzeRevealed ? "animate-in fade-in slide-in-from-bottom-2 duration-500" : undefined}>
 <AiAnalysisCard
 data={aiAnalysis!}
 media={floatingThumbs}
 onReanalyze={role === "admin" ? () => handleAnalyzeWithAI(true) : undefined}
 />
 </div>
 ) : analyzeFailed || attempt === "failed" ? (
 besideThumbs(
 <div className="flex items-center justify-between gap-2 p-3 rounded-xl border border-[#b6dded] bg-muted/30">
 <span className="text-xs text-muted-foreground">
 {t("pests.scouting.aiAnalysisError","No se pudo analizar las fotos.")}
 </span>
 {!isWorker && (
 <Button
 size="sm"
 onClick={() => handleAnalyzeWithAI()}
 className="shrink-0 bg-white text-black border border-[#b6dded] shadow-sm hover:bg-slate-50"
 >
 {t("common.retry","Reintentar")}
 </Button>
 )}
 </div>,
 )
 ) : (() => {
 /* «Sana» y «no se puede saber» (IA con la lista del cultivo) se dicen tal
    cual: antes caían aquí y salía «No se reconoció ninguna plaga», que con
    una foto que no deja ver nada es justo lo contrario de lo que dijo la IA.
    Un análisis viejo o sin lista, el texto de siempre. */
 const lecturaSinPlaga = leerDesenlace(aiAnalysis);
 return besideThumbs(
 lecturaSinPlaga && (lecturaSinPlaga.desenlace === "sana" || lecturaSinPlaga.desenlace === "no_se_sabe") ? (
 <AiNoPestMessage
 lectura={lecturaSinPlaga}
 fallback={{ title: t("pests.scouting.aiNoneDetected","No se reconoció ninguna plaga en las fotos.") }}
 />
 ) : (
 <p className="text-sm text-muted-foreground">
 {t("pests.scouting.aiNoneDetected","No se reconoció ninguna plaga en las fotos.")}
 </p>
 ),
 );
 })()}
 <ImageLightbox
 images={allPhotos}
 index={lightboxIndex ?? 0}
 open={lightboxIndex !== null}
 onOpenChange={(o) => !o && setLightboxIndex(null)}
 />
 </div>
 );
 })()}


 {/* Pest Observations — only after classification AND only when
 legacy observations actually exist. Worker quick-reports no
 longer create rows in pest_scouting_observations, so the
 empty"Sin observaciones registradas" state is suppressed. */}
 {settled && triage?.triage_status ==="classified" && !loading && observations.length > 0 && (
 <>
 <Separator />
 <div>
 <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
 <Bug className="w-5 h-5 text-primary" />
 {t("teams.pestObservations")}
 </h3>

 <div className="space-y-3">
 {observations.map((obs) => (
 <div
 key={obs.id}
 className="p-4 border rounded-lg bg-card hover:shadow-md transition-shadow group relative border-[#b6dded]"
 >
 {canManage && !isWorker && (
 <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
 <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeletingObservation(obs)} title={t("pests.scouting.deleteObservation")}>
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 )}

 <div className="flex items-start justify-between pr-16">
 <div className="flex items-center gap-3">
 <span className="text-2xl">{getTypeIcon(obs.pest?.type || null)}</span>
 <div>
 <p className="font-semibold text-foreground">
 {obs.pest ? pestTranslation.getName(obs.pest) : ((obs as any).pest_name_snapshot ||"Unknown Pest")}
 </p>
 <p className="text-sm text-muted-foreground capitalize">
 {obs.pest?.type ? t(`pests.pestTypes.${obs.pest.type}`, obs.pest.type) : t("common.unknown")}
 </p>
 </div>
 </div>
 </div>

 {(obs.observed_row_start != null || obs.observed_row_end != null) && (
 <div className="mt-2">
 <Badge variant="outline" className="text-xs">
 <Rows className="w-3 h-3 mr-1" />
 {obs.observed_row_start != null && obs.observed_row_end != null
 ? t("planner.rowRange", { start: obs.observed_row_start, end: obs.observed_row_end })
 : obs.observed_row_start != null
 ? `${t("pests.scouting.observedRowStart")}: ${obs.observed_row_start}`
 : `${t("pests.scouting.observedRowEnd")}: ${obs.observed_row_end}`}
 </Badge>
 </div>
 )}

 {obs.severity && (
 <div className="mt-3">
 <span className={PILL_BASE} style={{ backgroundColor: getSeverityColor(obs.severity) }}>
 {t(`worker.scouting.severityLevels.${obs.severity}`, obs.severity)}
 </span>
 </div>
 )}

 {obs.notes && (
 <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
 {obs.notes}
 </p>
 )}
 </div>
 ))}
 </div>
 </div>
 </>
 )}
 </div>
 </div>

 {/* El pie llega con la misma consulta, así que se reserva su alto: si no,
 aparecía de golpe al terminar de subir y daba un último salto. */}
 {!mediaReady && canTriage && !isWorker && (
 <div aria-hidden className={"flex-shrink-0 border-t bg-white px-4 pt-3.5 " + SHEET_SAFE_PB_CLASS}>
 <Skeleton className="h-[52px] w-full rounded-[14px]" />
 </div>
 )}

 {/* Sticky triage footer (managers) */}
 {mediaReady && triage && canTriage && !isWorker && (
 <div className={"flex-shrink-0 border-t bg-white backdrop-blur px-4 pt-3.5 " + SHEET_SAFE_PB_CLASS}>
 <ScoutingTriagePanel
 sessionId={session.id}
 state={triage}
 aiAnalysis={aiAnalysis}
 onChanged={() => {
 fetchPhotoUrl();
 fetchObservations();
 invalidateScoutingData(queryClient);
 onSessionUpdated?.();
 }}
 onCreateTreatment={
 onCreateTreatment
 ? () => {
 onCreateTreatment({
 ...session,
 // Forward triage fields so the parent can prefill
 // pest_id / pest_name without re-fetching.
 triage_pest_id: triage?.triage_pest_id ?? null,
 triage_pest_name_snapshot: triage?.triage_pest_name_snapshot ?? null,
 } as any);
 onOpenChange(false);
 }
 : undefined
 }
 />
 </div>
 )}

 {/* Worker-only sticky footer — single"Marcar resuelto" action.
 Workers never see delete/classify/dismiss controls. */}
 {isWorker && (
 <div className={"flex-shrink-0 border-t bg-white backdrop-blur px-4 pt-3.5 " + SHEET_SAFE_PB_CLASS}>
 <button
 type="button"
 className={ACTION_CLASS}
 style={actionStyle(ACTION_COLORS.blue.bg, ACTION_COLORS.blue.glow)}
 disabled={!!workerResolvedAt}
 onClick={async () => {
 try {
 const { error } = await supabase
 .from("pest_scouting_sessions")
 .update({ worker_resolved_at: new Date().toISOString() } as any)
 .eq("id", session.id);
 if (error) throw error;
 toast({ title: t("pests.scouting.triage.resolved") });
 setWorkerResolvedAt(new Date().toISOString());
 invalidateScoutingData(queryClient);
 onSessionUpdated?.();
 } catch (err) {
 console.error("Worker resolve failed:", err);
 toast({ title: t("common.error"), variant:"destructive" });
 }
 }}
 >
 <CheckCircle2 className="w-[18px] h-[18px] shrink-0" />
 <span className="truncate">{workerResolvedAt ? t("worker.incident.markedResolvedBadge") : t("pests.scouting.triage.markResolved")}</span>
 </button>
 </div>
 )}
 </DrawerContent>
 </Drawer>

 {canActSolo && (
 <>
 <FocusUpdateDialog focus={soloFocus ?? null} open={soloUpdateOpen} onOpenChange={(o) => { setSoloUpdateOpen(o); if (!o) onSessionUpdated?.(); }} />
 <RequestFollowUpDialog focus={soloFocus ?? null} open={soloFollowOpen} onOpenChange={setSoloFollowOpen} />
 </>
 )}

 {/* Delete Session Confirmation */}
 <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>{t("pests.scouting.deleteSessionTitle")}</AlertDialogTitle>
 <AlertDialogDescription>
 {t("pests.scouting.deleteSessionDescription")}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel disabled={isDeleting}>{t("common.cancel")}</AlertDialogCancel>
 <AlertDialogAction
 onClick={handleDeleteSession}
 disabled={isDeleting}
 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
 >
 {isDeleting ? t("common.deleting") : t("common.delete")}
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Delete Single Observation Dialog */}
 <AlertDialog open={!!deletingObservation} onOpenChange={() => setDeletingObservation(null)}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>{t("pests.scouting.deleteObservationConfirm")}</AlertDialogTitle>
 <AlertDialogDescription>
 {deletingObservation?.pest ? pestTranslation.getName(deletingObservation.pest) :""}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel disabled={isDeletingObservation}>{t("common.cancel")}</AlertDialogCancel>
 <AlertDialogAction
 onClick={handleDeleteSingleObservation}
 disabled={isDeletingObservation}
 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
 >
 {isDeletingObservation ? t("common.deleting") : t("common.delete")}
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>
 </>
 );
}
